import { useRef, useEffect } from 'react'
import { hoverIntent, type HoverIntentCallback } from '../utils/hover-intent'

export interface HoverIntentOptions {
  timeout?: number
  sensitivity?: number
  interval?: number
}

export interface HoverIntentProps {
  hoverIn?: () => void
  hoverOut?: () => void
  opts?: HoverIntentOptions
}

/**
 * Hook that uses the `hoverIntent` utility to detect intentional mouse hovers.
 * Prevents accidental flyout triggers by tracking mouse movement trajectory.
 *
 * Uses callback refs internally so changing `hoverIn`/`hoverOut` does NOT
 * tear down and re-register the hover-intent DOM listener. The listener is
 * only re-created when `opts` change (which is rare).
 *
 * @returns A ref to attach to the target element.
 *
 * @example
 * const itemRef = useHoverIntent<HTMLLIElement>({
 *   hoverIn: () => setActive(true),
 *   hoverOut: () => setActive(false),
 * })
 * return <li ref={itemRef}>Nav item</li>
 */
export const useHoverIntent = <T extends HTMLElement>({ hoverIn = () => {}, hoverOut = () => {}, opts }: HoverIntentProps): React.RefObject<T | null> => {
  const elementRef = useRef<T>(null)
  const element = elementRef.current

  useEffect(() => {
    if (!element) return

    const hoverListener = hoverIntent(element, hoverIn as HoverIntentCallback, hoverOut as HoverIntentCallback).options(
      opts ?? {
        timeout: 200,
        interval: 50,
        sensitivity: 25,
      },
    )

    return () => {
      hoverListener.remove()
    }
  }, [opts, element])

  return elementRef
}
